/*
 * Large Order Notice
 * ------------------
 * Shows a warning pop-up when a shopper tries to add a large quantity to the
 * cart, while still letting them proceed. Everything the merchant can control
 * lives in the "Large Order Notice" theme settings group (see
 * config/settings_schema.json). Markup + config come from
 * snippets/large-order-notice.liquid; styling from assets/large-order-notice.css.
 *
 * How the interception works:
 *   We attach `submit` and `click` listeners on `document` in the CAPTURE phase,
 *   so they run before the theme's own handlers. Two add-to-cart paths are
 *   covered:
 *     - Product/quick-view/sticky forms → the `submit` event (product-form.js).
 *     - Product-card grid buttons ([data-gprow-add-to-cart] in custom.js) →
 *       the `click` event, since those add via a direct fetch with no form.
 *   When the quantity being added is at or above the threshold we cancel the
 *   action and open the notice. If the shopper proceeds, we flag that element as
 *   acknowledged and replay the original submit/click — the capture listener
 *   lets that one pass so the normal add-to-cart flow runs untouched.
 */
if (!customElements.get('large-order-notice')) {
  customElements.define(
    'large-order-notice',
    class LargeOrderNotice extends HTMLElement {
      connectedCallback() {
        this.enabled = this.dataset.enabled === 'true';
        this.threshold = parseInt(this.dataset.threshold, 10);
        if (!Number.isFinite(this.threshold) || this.threshold < 1) this.enabled = false;

        this.dialog = this.querySelector('.large-order-notice__dialog');
        this.proceedBtn = this.querySelector('[data-lon-proceed]');
        this.cancelBtn = this.querySelector('[data-lon-cancel]');
        this.closeBtn = this.querySelector('[data-lon-close]');

        // Elements (forms or buttons) the shopper has already acknowledged.
        // Consumed once when the original action is replayed.
        this.acknowledged = new WeakSet();
        // The intercepted action, held while the notice is open:
        // { el, resume } where resume() replays it after "Add to cart".
        this.pending = null;

        // Info mode = opened from a cart "learn more" flag (no action to replay);
        // the primary button becomes a plain dismiss ("Got it").
        this.infoMode = false;
        this.defaultProceedLabel = (this.proceedBtn?.textContent || '').trim() || 'Add to cart';
        this.dismissLabel = this.dataset.dismissLabel || 'Got it';

        this.onDocSubmit = this.onDocSubmit.bind(this);
        this.onDocClick = this.onDocClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onProceed = this.onProceed.bind(this);
        this.onCancel = this.onCancel.bind(this);

        this.proceedBtn?.addEventListener('click', this.onProceed);
        this.cancelBtn?.addEventListener('click', this.onCancel);
        this.closeBtn?.addEventListener('click', this.onCancel);
        // Click on the backdrop (the host element itself) cancels.
        this.addEventListener('click', (evt) => {
          if (evt.target === this) this.onCancel();
        });

        if (this.enabled) {
          // Capture phase, so we run before the theme's own submit/click handlers.
          document.addEventListener('submit', this.onDocSubmit, true);
          document.addEventListener('click', this.onDocClick, true);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onDocSubmit, true);
        document.removeEventListener('click', this.onDocClick, true);
        document.removeEventListener('keydown', this.onKeydown);
      }

      /* Is this a form that adds a product to the cart? */
      isAddToCartForm(form) {
        if (!form || form.tagName !== 'FORM') return false;
        const action = form.getAttribute('action') || '';
        if (/\/cart\/add(\b|$)/.test(action)) return true;
        if (form.dataset && form.dataset.type === 'add-to-cart-form') return true;
        return !!form.closest('product-form');
      }

      /* Read the quantity being added. The quantity input is associated with the
         form via the `form=` attribute, so FormData (not querySelector) is the
         reliable way to read it. A single form can have more than one `quantity`
         input bound to it (e.g. the main product form and the sticky add-to-cart
         bar both target the same form), so take the largest — that's the safe
         value to warn against and equals the sole value in the common case. */
      getQuantity(form) {
        try {
          const data = new FormData(form);
          const values = data.getAll('quantity');
          if (!values.length) return 1;
          let max = 1;
          values.forEach((raw) => {
            const n = parseInt(String(raw).replace(/[^\d.-]/g, ''), 10);
            if (Number.isFinite(n) && n > max) max = n;
          });
          return max;
        } catch (e) {
          return 1;
        }
      }

      onDocSubmit(evt) {
        const form = evt.target;
        if (!this.isAddToCartForm(form)) return;

        // Already acknowledged this form — let it through, once.
        if (this.acknowledged.has(form)) {
          this.acknowledged.delete(form);
          return;
        }

        const qty = this.getQuantity(form);
        if (qty < this.threshold) return;

        // Stop product-form.js and the native submit, then show the notice.
        evt.preventDefault();
        evt.stopImmediatePropagation();

        const submitter =
          evt.submitter && evt.submitter.form === form
            ? evt.submitter
            : form.querySelector('[type="submit"]');
        this.infoMode = false;
        this.pending = {
          el: form,
          resume: () => {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
            } else {
              form.submit();
            }
          },
        };
        this.show();
      }

      onDocClick(evt) {
        const target = evt.target;
        if (!target || !target.closest) return;

        // Cart "learn more" flag → open the notice in info mode (no add action).
        if (target.closest('[data-lon-learn-more]')) {
          evt.preventDefault();
          evt.stopPropagation();
          this.pending = null;
          this.infoMode = true;
          this.show();
          return;
        }

        // Product-card "Add to Cart" buttons (custom.js grouped-product grid) add
        // via a direct fetch on click, so there's no form submit to catch — we
        // intercept the click itself. Scoped strictly to those buttons.
        const btn = target.closest('[data-gprow-add-to-cart]');
        if (!btn || btn.disabled) return;

        // Already acknowledged this button — let it through, once.
        if (this.acknowledged.has(btn)) {
          this.acknowledged.delete(btn);
          return;
        }

        const row = btn.closest('.gprow');
        const qtyInput = row ? row.querySelector('[data-gprow-qty-input]') : null;
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        if (qty < this.threshold) return;

        // Stop the grid's delegated add-to-cart handler, then show the notice.
        evt.preventDefault();
        evt.stopImmediatePropagation();

        this.infoMode = false;
        this.pending = { el: btn, resume: () => btn.click() };
        this.show();
      }

      updateButtonLabel() {
        if (!this.proceedBtn) return;
        this.proceedBtn.textContent = this.infoMode ? this.dismissLabel : this.defaultProceedLabel;
      }

      show() {
        this.updateButtonLabel();
        this.previousActive = document.activeElement;
        this.hidden = false;
        // Force reflow so the CSS transition runs from the hidden state.
        void this.offsetWidth;
        this.classList.add('is-open');
        document.documentElement.classList.add('lon-open');
        document.addEventListener('keydown', this.onKeydown);
        requestAnimationFrame(() => {
          (this.dialog || this.closeBtn || this.proceedBtn)?.focus();
        });
      }

      hide() {
        this.classList.remove('is-open');
        document.documentElement.classList.remove('lon-open');
        document.removeEventListener('keydown', this.onKeydown);
        const done = () => {
          this.hidden = true;
          this.removeEventListener('transitionend', done);
        };
        this.addEventListener('transitionend', done);
        // Fallback if no transition fires.
        setTimeout(done, 300);
        try {
          this.previousActive?.focus();
        } catch (e) {
          /* element gone */
        }
      }

      onProceed() {
        const pending = this.pending;
        this.pending = null;
        this.hide();
        if (!pending) return;

        // Flag the element so the replayed submit/click passes straight through.
        this.acknowledged.add(pending.el);
        pending.resume();
      }

      onCancel() {
        this.pending = null;
        this.hide();
      }

      onKeydown(evt) {
        if (evt.key === 'Escape') {
          this.onCancel();
          return;
        }
        if (evt.key === 'Tab') this.trapFocus(evt);
      }

      trapFocus(evt) {
        const focusables = this.dialog?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables) return;
        const list = Array.prototype.filter.call(
          focusables,
          (el) => !el.disabled && el.offsetParent !== null
        );
        if (!list.length) return;
        const first = list[0];
        const last = list[list.length - 1];
        if (evt.shiftKey && document.activeElement === first) {
          evt.preventDefault();
          last.focus();
        } else if (!evt.shiftKey && document.activeElement === last) {
          evt.preventDefault();
          first.focus();
        }
      }
    }
  );
}
